import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react'
import dayjs from 'dayjs'
import GlobalContext from '../../../../../../context/global-context'
import { OrganizationAPI } from '../../../../../../api/organization.api'
import { CentroCostosAPI } from '../../../../../../api/centroCostos.api'
import {
  activosFijos,
  activosIntangibles,
  existencias,
  getExpenseTypeKeyFromAccountCode
} from '../../../../data/dataProveedor'
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined'
import PlanRestrictedButton from '../../../../ui/PlanRestrictedButton'

const CENTRO_COSTO_TEMPORALIDAD = 3

const CENTER_COLORS = [
  '#00C6B3',
  '#FF6B6B',
  '#4F46E5',
  '#F59E0B',
  '#EC4899',
  '#10B981',
  '#3B82F6',
  '#A855F7',
  '#F97316',
  '#22C55E'
]

const buildCentroCostoPayload = rows =>
  rows
    .filter(row => row.centroCostoId && Number(row.porcentaje) > 0)
    .map(row => ({
      idCentroCosto: Number(row.centroCostoId),
      porcentajeCentroCosto: Number(row.porcentaje)
    }))

const buildEmptyCentroCostoRow = (index = 0) => ({
  centroCostoId: '',
  porcentaje: '',
  color: CENTER_COLORS[index % CENTER_COLORS.length]
})

const isForbiddenCentroName = name =>
  name?.trim().toLowerCase() === 'estado de resultados consolidado'

const CircleProgress = ({ rows }) => {
  const total = Math.min(
    100,
    rows.reduce((acc, row) => acc + (Number(row.porcentaje) || 0), 0)
  )

  let offset = 0
  const stops = []

  rows.forEach(row => {
    const pct = Math.max(0, Math.min(100 - offset, Number(row.porcentaje) || 0))
    if (pct <= 0) return
    const color = row.color || '#00C6B3'
    stops.push(`${color} ${offset}% ${offset + pct}%`)
    offset += pct
  })

  if (offset < 100) {
    stops.push(`#E5E7EB ${offset}% 100%`)
  }

  const background = `conic-gradient(${stops.join(', ')})`

  return (
    <div className="flex flex-col items-center mt-3">
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center"
        style={{ background }}
      >
        <span className="text-xs font-semibold">{total}%</span>
      </div>
      <span className="mt-1 text-[11px] text-gray-500 text-center font-sans">
        Distribución % centros de costo
      </span>
    </div>
  )
}

const expenseTypeOptions = [
  { id: 'Gastos', nombreCuenta: 'Gastos' },
  { id: 'Existencias', nombreCuenta: 'Existencias' },
  { id: 'Activos Fijos', nombreCuenta: 'Activos Fijos' },
  { id: 'Activos Intangibles', nombreCuenta: 'Activos Intangibles' }
]

const EXPENSE_TYPE_KEY_BY_LABEL = {
  Gastos: 'gastos',
  Existencias: 'existensias',
  'Activos Fijos': 'activos-fijos',
  'Activos Intangibles': 'activos-intangibles'
}

const EXPENSE_TYPE_LABEL_BY_KEY = {
  gastos: 'Gastos',
  existensias: 'Existencias',
  'activos-fijos': 'Activos Fijos',
  'activos-intangibles': 'Activos Intangibles'
}

const toNumericId = (value) => {
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : null
}

const getComparableCuentaId = (value) => {
  if (value === null || value === undefined || value === '') return null
  return toNumericId(value) ?? String(value)
}

const getCuentaOptionId = (cuenta) =>
  getComparableCuentaId(cuenta?.id_key) ??
  getComparableCuentaId(cuenta?.id) ??
  cuenta?.id_key ??
  cuenta?.id ??
  null

const getExpenseTypeKeyFromAccountId = (accountId) => {
  const normalizedAccountId = toNumericId(accountId)

  if (normalizedAccountId == null) return 'gastos'
  if (existencias.includes(normalizedAccountId)) return 'existensias'
  if (activosFijos.includes(normalizedAccountId)) return 'activos-fijos'
  if (activosIntangibles.includes(normalizedAccountId)) return 'activos-intangibles'

  return 'gastos'
}

const CustomSelect = ({
  label,
  name,
  options,
  value,
  onChange,
  disabled,
  includeEmptyOption = false
}) => (
  <div className="relative flex flex-col items-start">
    {label && (
      <label
        htmlFor={name}
        className="absolute top-[-15px] left-4 bg-transparent text-xs font-medium text-gray-500"
      >
        {label}
      </label>
    )}
    <select
      className="h-9 w-full appearance-none focus:outline-none border border-gray-300 hover:border-gray-400 focus:border-gray-400 px-4 py-2 rounded-full shadow leading-tight text-xs"
      name={name}
      onChange={onChange}
      value={value}
      disabled={disabled}
    >
      {includeEmptyOption && <option value=""></option>}
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.nombreCuenta}
        </option>
      ))}
    </select>
  </div>
)

const CustomNumberInput = ({ label, name, value, onChange, disabled, placeholder = '' }) => (
  <div className="relative flex flex-col items-start">
    {label && (
      <label
        htmlFor={name}
        className="absolute top-[-15px] left-4 bg-transparent text-xs font-medium text-gray-500"
      >
        {label}
      </label>
    )}
    <input
      className="h-9 w-full focus:outline-none border border-gray-300 hover:border-gray-400 focus:border-gray-400 px-4 py-2 rounded-full shadow leading-tight text-xs"
      type="number"
      name={name}
      min="0"
      step="1"
      inputMode="numeric"
      placeholder={placeholder}
      onChange={onChange}
      value={value}
      disabled={disabled}
    />
  </div>
)

const ClassificationSection = ({
  type,
  validation,
  clasificacionOptions,
  selectedCuentaVenta,
  onOptionSelect,
  selectedType,
  onTypeSelect,
  selectedClasificador,
  onClasificadorSelect,
  selectedCuentaVentaExtra,
  onCuentaVentaExtraSelect,
  selectedClasificadorExtra,
  onClasificadorExtraSelect,
  idEmpresa
}) => (
  <>
    {type === 'venta' ? (
      <div className="flex flex-col mt-3">
        <CustomSelect
          label=""
          name="idCuentaVenta"
          options={clasificacionOptions}
          value={selectedCuentaVenta || ''}
          onChange={onOptionSelect}
          includeEmptyOption
          disabled={validation === -5 || clasificacionOptions.length === 0}
        />
        <div className="mt-3 w-full py-2">
          <CustomNumberInput
            label="Dias de vencimiento"
            name="diasVencimientoIngreso"
            value={selectedCuentaVentaExtra}
            onChange={onCuentaVentaExtraSelect}
            placeholder="Escribe un numero"
            disabled={validation === -5 || !idEmpresa}
          />
        </div>
      </div>
    ) : (
      <>
        <div className="flex flex-col mt-3">
          <CustomSelect
            label="Tipo"
            name="selectedType"
            options={expenseTypeOptions}
            value={selectedType}
            onChange={onTypeSelect}
            disabled={validation === -5}
          />
        </div>

        <div className="flex flex-col mt-3">
          <CustomSelect
            label="Clasificacion"
            name="idCuentaClasificador"
            options={clasificacionOptions}
            value={selectedClasificador || ''}
            onChange={onClasificadorSelect}
            includeEmptyOption
            disabled={validation === -5}
          />
          <div className="mt-3 w-full py-2">
            <CustomNumberInput
              label="Dias de vencimiento"
              name="diasVencimientoEgreso"
              value={selectedClasificadorExtra}
              onChange={onClasificadorExtraSelect}
              placeholder="Escribe un numero"
              disabled={validation === -5 || !idEmpresa}
            />
          </div>
        </div>
      </>
    )}
  </>
)

const getDiasInputValue = diasVencimiento =>
  diasVencimiento == null || diasVencimiento === '' ? '' : String(diasVencimiento)

const ModalEdit = forwardRef(function ModalEdit(
  {
    open,
    setOpen,
    idLibro,
    type,
    status,
    setSwitchGetData,
    title,
    aprobado,
    idEmpresa,
    diasVencimiento
  },
  ref
) {
  const [data, setData] = useState({
    validation: ''
  })
  const { ui, currentBusiness } = useContext(GlobalContext)
  const organizationAPI = new OrganizationAPI()
  const centroCostosAPI = new CentroCostosAPI()
  const [, setSwitchDate] = useState(false)
  const [comentario] = useState(data.glosa || '')
  const [selectedCuentaVenta, setSelectedCuentaVenta] = useState(null)
  const [selectedCuentaVentaExtra, setSelectedCuentaVentaExtra] = useState('')
  const [initialized, setInitialized] = useState(false)
  const [selectedType, setSelectedType] = useState('Gastos')
  const [selectedClasificador, setSelectedClasificador] = useState(null)
  const [selectedClasificadorExtra, setSelectedClasificadorExtra] = useState('')
  const [dataContable, setDataContable] = useState([])
  const [centroCostos, setCentroCostos] = useState([])
  const [centrosCostoRows, setCentrosCostoRows] = useState([buildEmptyCentroCostoRow()])
  const [initialCentroCostoPayload, setInitialCentroCostoPayload] = useState([])
  const [centroCostoError, setCentroCostoError] = useState('')
  const [centroCostoInitialized, setCentroCostoInitialized] = useState(false)
  const [, setReload] = useState(false)
  const [, setLoading] = useState(false)

  const handleClose = () => {
    if (typeof setOpen === 'function') {
      setOpen(false)
    }
  }

  const functionGetDataLibroContable = async (overrideType = null) => {
    if (!currentBusiness?.id || !idLibro || !type) return

    try {
      setLoading(true)
      const response = await organizationAPI.getDataLibroContable(currentBusiness.id, idLibro, type)
      const libroData = response?.data ?? {}
      let selectedTypeKey = 'ingreso'

      if (type !== 'venta') {
        if (overrideType) {
          selectedTypeKey = EXPENSE_TYPE_KEY_BY_LABEL[overrideType] || 'gastos'
          setSelectedType(overrideType)
        } else {
          selectedTypeKey =
            getExpenseTypeKeyFromAccountCode(
              libroData?.codigoCuenta ?? libroData?.cuentaContable?.codigoCuenta
            ) ?? getExpenseTypeKeyFromAccountId(libroData?.idCuenta)
          setSelectedType(EXPENSE_TYPE_LABEL_BY_KEY[selectedTypeKey] || 'Gastos')
        }
      }

      const responseCuentas = await organizationAPI.getCuentasContables({
        businessId: currentBusiness.id,
        type: selectedTypeKey
      })
      setSwitchDate(false)
      setData(libroData)
      setDataContable(responseCuentas?.data ?? [])
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    try {
      ui.setLoader({ visible: true })
      setCentroCostoError('')
      const cuentaContableId = type === 'venta' ? selectedCuentaVenta : selectedClasificador
      const diasVencimientoValue =
        type === 'venta' ? selectedCuentaVentaExtra : selectedClasificadorExtra
      const updateData = {
        cuentaContableId: getComparableCuentaId(cuentaContableId),
        glosa: comentario,
        vencimiento: new Date(data.fechaVencimiento).toISOString(),
        validation: 3
      }
      const centroCostoPayload = buildCentroCostoPayload(centrosCostoRows)
      const hasCentroCostoChanges =
        JSON.stringify(centroCostoPayload) !== JSON.stringify(initialCentroCostoPayload)

      if (hasCentroCostoChanges && centroCostoPayload.length > 0) {
        const totalPorcentaje = centroCostoPayload.reduce(
          (total, item) => total + item.porcentajeCentroCosto,
          0
        )

        if (totalPorcentaje !== 100) {
          setCentroCostoError('La suma de los porcentajes de centros de costo debe ser 100%.')
          return false
        }

        const centroCostoIds = centroCostoPayload.map(item => item.idCentroCosto)
        if (new Set(centroCostoIds).size !== centroCostoIds.length) {
          setCentroCostoError('No puedes repetir un centro de costo.')
          return false
        }
      }

      const requests = [
        organizationAPI.patchDataLibroContable(currentBusiness?.id, idLibro, updateData)
      ]

      if (hasCentroCostoChanges) {
        requests.push(
          centroCostosAPI.createLibroCentroCosto(
            currentBusiness?.id,
            idLibro,
            centroCostoPayload,
            CENTRO_COSTO_TEMPORALIDAD
          )
        )
      }

      await Promise.all(requests)

      if (diasVencimientoValue !== '' && idEmpresa) {
        const diasPayload = {
          diasVencimiento: Number(diasVencimientoValue)
        }

        if (type === 'venta' || type === 'compra') {
          await organizationAPI.patchEmpresaSiiDiasVencimiento(
            currentBusiness?.id,
            idEmpresa,
            diasPayload
          )
        }

        if (type === 'honorario') {
          await organizationAPI.patchEmisorDiasVencimiento(
            currentBusiness?.id,
            idEmpresa,
            diasPayload
          )
        }
      }

      setSwitchGetData(true)
      functionGetDataLibroContable()
      handleClose()
      return true
    } catch (error) {
      console.error(error)
      return false
    } finally {
      ui.setLoader({ visible: false })
    }
  }

  useEffect(() => {
    if (currentBusiness?.id && idLibro && type) {
      setCentroCostoInitialized(false)
      setInitialized(false)
      setSelectedCuentaVenta(null)
      setSelectedClasificador(null)
      setData({ validation: '' })
      functionGetDataLibroContable()
    }
  }, [currentBusiness?.id, idLibro, type])

  useEffect(() => {
    if (!currentBusiness?.id) return

    const getCentroCostos = async () => {
      try {
        const response = await centroCostosAPI.getCentroCostos(currentBusiness.id)
        setCentroCostos(Array.isArray(response?.data) ? response.data : [])
      } catch (error) {
        console.error('Error al cargar los centros de costo:', error)
        setCentroCostos([])
      }
    }

    getCentroCostos()
  }, [currentBusiness?.id])

  useEffect(() => {
    if (centroCostoInitialized || typeof data.centro === 'undefined') return

    const rows =
      Array.isArray(data.centro) && data.centro.length > 0
        ? data.centro.map((item, index) => ({
            centroCostoId: String(item.centroCostoId),
            porcentaje: item.porcentajeCentroCosto ?? '',
            color: CENTER_COLORS[index % CENTER_COLORS.length]
          }))
        : [buildEmptyCentroCostoRow()]

    setCentrosCostoRows(rows)
    setInitialCentroCostoPayload(buildCentroCostoPayload(rows))
    setCentroCostoInitialized(true)
  }, [centroCostoInitialized, data.centro])

  useEffect(() => {
    const initialDiasValue = getDiasInputValue(diasVencimiento)

    if (type === 'venta') {
      setSelectedCuentaVentaExtra(initialDiasValue)
      setSelectedClasificadorExtra('')
      return
    }

    setSelectedCuentaVentaExtra('')
    setSelectedClasificadorExtra(initialDiasValue)
  }, [diasVencimiento, idLibro, type])

  useImperativeHandle(ref, () => ({
    submit: handleSubmit
  }))

  const handleOptionSelect = (e) => {
    if (e?.target) {
      const { name, value } = e.target

      setData((prevData) => ({
        ...prevData,
        [name]: name === 'validation' ? Number(value) : value
      }))

      switch (name) {
        case 'idCuentaVenta':
          setSelectedCuentaVenta(getComparableCuentaId(value))
          break
        case 'idCuentaClasificador':
          setSelectedClasificador(getComparableCuentaId(value))
          break
        default:
          break
      }
    } else {
      const newDate = dayjs(e)
      const formattedDate = newDate.format('YYYY-MM-DD')
      setData((prevData) => ({
        ...prevData,
        fechaVencimiento: formattedDate
      }))
    }
  }

  useEffect(() => {
    if (data.linkage) {
      setReload((prev) => !prev)
    }
  }, [data])

  useEffect(() => {
    if (data && data.validation === undefined && aprobado != null && type === 'venta') {
      setData((prevData) => ({
        ...prevData,
        validation: aprobado
      }))
    }
  }, [data, aprobado, type])

  useEffect(() => {
    if (data && dataContable.length > 0 && !initialized) {
      const cuentaSeleccionada = dataContable.find((cuenta) => cuenta.name === data.nombreCuenta)
      const cuentaSeleccionadaPorId = dataContable.find(
        (cuenta) => getCuentaOptionId(cuenta) === getComparableCuentaId(data.idCuenta)
      )
      const selectedCuenta = cuentaSeleccionadaPorId || cuentaSeleccionada

      if (selectedCuenta) {
        const selectedCuentaId = getCuentaOptionId(selectedCuenta)
        if (type === 'venta') {
          setSelectedCuentaVenta(selectedCuentaId)
        } else {
          setSelectedClasificador(selectedCuentaId)
        }
      }
      setInitialized(true)
    }
  }, [data, dataContable, type, initialized])

  const handleTypeSelect = (e) => {
    const newType = e.target.value
    setSelectedType(newType)
    setSelectedClasificador(null)
    functionGetDataLibroContable(newType)
  }

  const handleClasificadorSelect = (e) => {
    setSelectedClasificador(getComparableCuentaId(e.target.value))
  }

  const handleCuentaVentaExtraSelect = (e) => {
    setSelectedCuentaVentaExtra(e.target.value)
  }

  const handleClasificadorExtraSelect = (e) => {
    setSelectedClasificadorExtra(e.target.value)
  }

  const filteredOptions = dataContable.map((item) => ({
    id: getCuentaOptionId(item),
    nombreCuenta: item.name
  }))

  const centroCostoOptions = centroCostos
    .filter(centroCosto => !isForbiddenCentroName(centroCosto.name))
    .map(centroCosto => ({
      id: centroCosto.id,
      nombreCuenta: centroCosto.name
    }))

  const handleCentroCostoRowChange = (index, field, value) => {
    setCentrosCostoRows(rows =>
      rows.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              [field]:
                field === 'porcentaje'
                  ? value === ''
                    ? ''
                    : Math.max(0, Math.min(100, Number(value) || 0))
                  : value
            }
          : row
      )
    )
    setCentroCostoError('')
  }

  const addCentroCostoRow = () => {
    if (centrosCostoRows.length >= centroCostoOptions.length) return
    setCentrosCostoRows(rows => [...rows, buildEmptyCentroCostoRow(rows.length)])
    setCentroCostoError('')
  }

  const removeCentroCostoRow = index => {
    setCentrosCostoRows(rows => rows.filter((_, rowIndex) => rowIndex !== index))
    setCentroCostoError('')
  }

  return (
    <>
      <div className="w-full">
        <div className="w-full">
          <section className="w-full p-4 border-2 rounded-3xl h-auto shadow-lg">
            <h2 className="text-sm font-semibold mb-4">Cuenta Clasificacion</h2>
            <div className="flex flex-wrap gap-6">
              <div className="flex flex-col w-full gap-2 mt-1">
                <ClassificationSection
                  type={type}
                  validation={data.validation}
                  clasificacionOptions={filteredOptions}
                  selectedCuentaVenta={selectedCuentaVenta}
                  onOptionSelect={handleOptionSelect}
                  selectedType={selectedType}
                  onTypeSelect={handleTypeSelect}
                  selectedClasificador={selectedClasificador}
                  onClasificadorSelect={handleClasificadorSelect}
                  selectedCuentaVentaExtra={selectedCuentaVentaExtra}
                  onCuentaVentaExtraSelect={handleCuentaVentaExtraSelect}
                  selectedClasificadorExtra={selectedClasificadorExtra}
                  onClasificadorExtraSelect={handleClasificadorExtraSelect}
                  idEmpresa={idEmpresa}
                />
                <div className="flex items-center gap-1 mt-1">
                  <div className="w-5 h-5 border border-black rounded-full flex items-center justify-center">
                    <span className="text-xs">!</span>
                  </div>
                  <p className="text-xs leading-tight">
                    Se aplica esta clasificacion a todos los registros.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 border-t border-gray-200 pt-5">
              <h2 className="text-sm font-semibold mb-5">Centros de costo</h2>

              <PlanRestrictedButton
                keepEnabled
                requiredPermission="allowCostCenter"
                message="Los centros de costos"
              >
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_150px] gap-6 items-center">
                  <div className="space-y-4">
                    {centrosCostoRows.map((row, index) => (
                      <div
                        key={`centro-costo-${index}`}
                        className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_180px_28px] gap-3 items-center"
                      >
                        <CustomSelect
                          label="Centro de costo"
                          name={`centroCosto-${index}`}
                          options={centroCostoOptions}
                          value={row.centroCostoId}
                          onChange={event =>
                            handleCentroCostoRowChange(index, 'centroCostoId', event.target.value)
                          }
                          includeEmptyOption
                          disabled={data.validation === -5}
                        />
                        <CustomNumberInput
                          label="Porcentaje"
                          name={`porcentaje-${index}`}
                          value={row.porcentaje}
                          onChange={event =>
                            handleCentroCostoRowChange(index, 'porcentaje', event.target.value)
                          }
                          disabled={data.validation === -5}
                        />
                        <button
                          type="button"
                          onClick={() => removeCentroCostoRow(index)}
                          className="text-red-500 hover:text-red-700"
                          title="Eliminar centro de costo"
                          disabled={data.validation === -5}
                        >
                          <ClearOutlinedIcon fontSize="small" />
                        </button>
                      </div>
                    ))}

                    <div className="flex items-center justify-between gap-4">
                      <button
                        type="button"
                        className="text-xs text-accent3 disabled:text-gray-400"
                        onClick={addCentroCostoRow}
                        disabled={
                          data.validation === -5 ||
                          centroCostoOptions.length === 0 ||
                          centrosCostoRows.length >= centroCostoOptions.length
                        }
                      >
                        + Agregar centro de costo
                      </button>
                      <span className="text-xs text-gray-500">
                        Se aplica a todos los registros.
                      </span>
                    </div>

                    {centroCostoError && (
                      <p className="text-xs text-red-500">{centroCostoError}</p>
                    )}
                  </div>

                  <CircleProgress rows={centrosCostoRows} />
                </div>
              </PlanRestrictedButton>
            </div>
          </section>
        </div>
      </div>
    </>
  )
})

export default ModalEdit
